// src/mobile_pages/system/UserList.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../api/axios";
import { fetchUsers } from "../../api/users/user";
import MobileBoardPagination from "../../components/common/board/MobileBoardPagination";
import {
  User,
  Building2,
  Briefcase,
  Phone,
  Mail,
  Search,
  UserPlus,
} from "lucide-react";

export default function UserList() {
  const navigate = useNavigate();
  const projectId = localStorage.getItem("projectId");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // 필터
  const [q, setQ] = useState("");
  const [company, setCompany] = useState("");
  const [department, setDepartment] = useState("");
  const [position, setPosition] = useState("");

  // 필터 옵션
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [positions, setPositions] = useState([]);

  // 페이지네이션
  const [page, setPage] = useState(1);
  const pageSize = 10;

  // 필터 패널 열기/닫기
  const [filterOpen, setFilterOpen] = useState(false);

  // ======================= API ===========================
  const loadUsers = async () => {
    setLoading(true);
    try {
      const res = await fetchUsers();
      let list = res.data?.results ?? res.data ?? [];

      // ===== 클라이언트에서 필터링 =====
      if (q.trim()) {
        list = list.filter((u) =>
          (u.first_name + u.last_name + u.email + u.username)
            .toLowerCase()
            .includes(q.toLowerCase())
        );
      }
      if (company)
        list = list.filter((u) => String(u.company_id) === String(company));
      if (department)
        list = list.filter(
          (u) => String(u.department_id) === String(department)
        );
      if (position)
        list = list.filter((u) => String(u.position_id) === String(position));

      setRows(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  // 필터 옵션 로드
  useEffect(() => {
    (async () => {
      try {
        const [c, d, p] = await Promise.all([
          api.get("companies/"),
          api.get("departments/"),
          api.get("positions/"),
        ]);

        setCompanies(c.data?.results ?? c.data ?? []);
        setDepartments(d.data?.results ?? d.data ?? []);
        setPositions(p.data?.results ?? p.data ?? []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  useEffect(() => {
    loadUsers();
  }, [q, company, department, position]);

  // 페이지네이션
  const pagedRows = rows.slice((page - 1) * pageSize, page * pageSize);
  const total = rows.length;

  // Skeleton Card
  const SkeletonCard = () => (
    <div className="bg-white rounded-lg p-3 border animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
      <div className="h-3 bg-gray-200 rounded w-3/4 mb-1"></div>
      <div className="h-3 bg-gray-200 rounded w-1/2"></div>
    </div>
  );

  return (
    <div className="mobile-page-root">
      <div className="mobile-page-root-inner space-y-3 mobile-bottom-spacing">
        {/* Header */}
        <div className="bg-white rounded-lg p-3 border">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-base font-bold text-gray-900">사용자 목록</h2>
              <p className="text-[10px] text-gray-500 mt-0.5">전체 {total}명</p>
            </div>
            <button
              onClick={() => navigate(`/project/${projectId}/system/user/add`)}
              className="btn-create-sm flex items-center gap-1 text-xs"
            >
              <UserPlus className="w-3 h-3" />
              추가
            </button>
          </div>
        </div>

        {/* Search & Filter */}
        <div className="bg-white rounded-lg p-3 border space-y-2">
          {/* Search Input */}
          <div className="flex items-center gap-2">
            <div className="flex-1 relative">
              <input
                className="w-full bg-gray-50 border border-gray-200 rounded-md pl-8 pr-3 py-2 text-xs focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="이름, 이메일, 아이디 검색"
                value={q}
                onChange={(e) => {
                  setQ(e.target.value);
                  setPage(1);
                }}
              />
              <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            </div>
            <button
              onClick={() => setFilterOpen(!filterOpen)}
              className={`btn-basic-sm text-xs px-3 ${filterOpen ? "bg-blue-50 border-blue-300" : ""}`}
            >
              필터
            </button>
          </div>

          {/* Filter Panel */}
          {filterOpen && (
            <div className="space-y-2 pt-2 border-t animate-fade-in-soft">
              <select
                className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs"
                value={company}
                onChange={(e) => {
                  setCompany(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">전체 회사</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>

              <select
                className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs"
                value={department}
                onChange={(e) => {
                  setDepartment(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">전체 부서</option>
                {departments.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>

              <select
                className="w-full bg-gray-50 border border-gray-200 rounded-md px-3 py-2 text-xs"
                value={position}
                onChange={(e) => {
                  setPosition(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">전체 직책</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>

              <button
                onClick={() => {
                  setQ("");
                  setCompany("");
                  setDepartment("");
                  setPosition("");
                  setPage(1);
                }}
                className="btn-basic-sm text-xs w-full"
              >
                필터 초기화
              </button>
            </div>
          )}
        </div>

        {/* User List */}
        <div className="space-y-2">
          {loading ? (
            <>
              <SkeletonCard />
              <SkeletonCard />
              <SkeletonCard />
            </>
          ) : pagedRows.length === 0 ? (
            <div className="bg-white rounded-lg p-8 text-center border">
              <User className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-sm text-gray-500">등록된 사용자가 없습니다.</p>
            </div>
          ) : (
            pagedRows.map((row) => (
              <button
                key={row.id}
                onClick={() => navigate(`/project/${projectId}/system/user/detail/${row.id}`)}
                className="w-full text-left bg-white rounded-lg p-3 border hover:border-blue-300 active:bg-gray-50 transition-all overflow-hidden"
              >
                <div className="flex items-start gap-3">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600 font-semibold text-sm flex-shrink-0">
                    {(row.last_name || row.first_name || "U").slice(0, 1)}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {row.last_name ?? ""}
                      {row.first_name ?? ""}
                      <span className="text-xs font-normal text-gray-400 ml-1">
                        @{row.username}
                      </span>
                    </h3>

                    <div className="space-y-0.5 mt-1">
                      {(row.company || row.department) && (
                        <div className="flex items-center gap-1">
                          <Building2 className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-600 truncate">
                            {row.company || "-"} · {row.department || "-"}
                          </span>
                        </div>
                      )}

                      {row.position && (
                        <div className="flex items-center gap-1">
                          <Briefcase className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-600 truncate">
                            {row.position}
                          </span>
                        </div>
                      )}

                      {row.phone_number && (
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-600 truncate">
                            {row.phone_number}
                          </span>
                        </div>
                      )}

                      {row.email && (
                        <div className="flex items-center gap-1">
                          <Mail className="w-3 h-3 text-gray-400 flex-shrink-0" />
                          <span className="text-xs text-gray-600 truncate">
                            {row.email}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="bg-white rounded-lg p-3 border">
            <MobileBoardPagination
              page={page}
              pageSize={pageSize}
              total={total}
              onPageChange={(p) => setPage(p)}
            />
          </div>
        )}
      </div>
    </div>
  );
}
